// Rate limiting handler with exponential backoff
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 60000; // 60 seconds
const MAX_RETRIES = 5;

export class RateLimitError extends Error {
    constructor(message, retryAfter = null) {
        super(message);
        this.name = 'RateLimitError';
        this.retryAfter = retryAfter;
    }
}

export async function fetchWithRetry(fetchFn, options = {}) {
    const {
        maxRetries = MAX_RETRIES,
        initialDelay = INITIAL_RETRY_DELAY,
        maxDelay = MAX_RETRY_DELAY,
        onRetry = null,
    } = options;

    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetchFn();
            
            // If successful, return the response
            if (response.status !== 429) {
                return response;
            }
            
            // Handle rate limit error
            const retryAfter = response.headers.get('Retry-After');
            const delay = calculateDelay(attempt, initialDelay, maxDelay, retryAfter);
            
            if (attempt === maxRetries) {
                throw new RateLimitError(
                    'Too many requests. Please try again later.',
                    retryAfter
                );
            }
            
            // Notify about retry if callback provided
            if (onRetry) {
                onRetry({
                    attempt: attempt + 1,
                    delay,
                    maxRetries,
                    error: 'Rate limit exceeded'
                });
            }
            
            // Wait before retrying
            await sleep(delay);
            
        } catch (error) {
            lastError = error;
            
            // If it's not a rate limit error, don't retry
            if (!(error instanceof RateLimitError) && error.message !== 'Too many requests') {
                throw error;
            }
            
            if (attempt === maxRetries) {
                throw error;
            }
        }
    }
    
    throw lastError || new Error('Max retries exceeded');
}

function calculateDelay(attempt, initialDelay, maxDelay, retryAfter) {
    // If server provides Retry-After header, use it
    if (retryAfter) {
        // Check if it's a number (delay in seconds) or a date
        const retryAfterMs = isNaN(retryAfter) 
            ? new Date(retryAfter).getTime() - Date.now()
            : parseInt(retryAfter) * 1000;
            
        if (retryAfterMs > 0) {
            return Math.min(retryAfterMs, maxDelay);
        }
    }
    
    // Otherwise use exponential backoff
    const exponentialDelay = initialDelay * Math.pow(2, attempt);
    
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.3 * exponentialDelay;
    
    return Math.min(exponentialDelay + jitter, maxDelay);
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// Rate limit status tracker
let rateLimitStatus = {
    isLimited: false,
    retryAfter: null,
    message: null
};

export function setRateLimitStatus(status) {
    rateLimitStatus = { ...rateLimitStatus, ...status };
}

export function getRateLimitStatus() {
    return rateLimitStatus;
}

export function clearRateLimitStatus() {
    rateLimitStatus = {
        isLimited: false,
        retryAfter: null,
        message: null
    };
}